import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/utils/supabase';
import type { Database } from '@beyond-silence/types';

type ArticleRow = Database['public']['Tables']['articles']['Row'];
type TranslationRow = Database['public']['Tables']['article_translations']['Row'];

export type ArticleStatus = ArticleRow['status'];
export type LanguageCode = 'fr' | 'en' | 'ar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cast<T>(data: any): T { return data as T; }

// ── Articles list ─────────────────────────────────────────────────
interface UseArticlesOptions { status?: ArticleStatus; limit?: number; }

export function useArticles({ status, limit = 50 }: UseArticlesOptions = {}) {
  const [articles, setArticles] = useState<(ArticleRow & { translations: TranslationRow[]; total_views?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    let query = supabase.from('articles')
      .select('*, translations:article_translations(*)')
      .order('updated_at', { ascending: false }).limit(limit);
    if (status) query = query.eq('status', status);
    const { data, error: err } = await query;
    if (err) { setError(err.message); setLoading(false); return; }

    const arts = cast<(ArticleRow & { translations: TranslationRow[] })[]>(data) ?? [];

    // Fetch view counts from article_kpis for engagement numbers
    const { data: kpis } = await supabase
      .from('article_kpis')
      .select('article_id, total_views');
    const viewsMap = new Map<string, number>();
    for (const k of (kpis ?? [])) {
      viewsMap.set((k as any).article_id, (k as any).total_views ?? 0);
    }

    // Merge view counts into articles
    const merged = arts.map(a => ({
      ...a,
      total_views: viewsMap.get(a.id) ?? 0,
    }));

    setArticles(merged);
    setLoading(false);
  }, [status, limit]);

  useEffect(() => { fetch(); }, [fetch]);
  return { articles, loading, error, refetch: fetch };
}

// ── Single article ────────────────────────────────────────────────
export function useArticle(articleId: string | undefined) {
  const [article, setArticle] = useState<ArticleRow | null>(null);
  const [translations, setTranslations] = useState<TranslationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!articleId) { setLoading(false); return; }
    (async () => {
      setLoading(true); setError(null);
      const { data: art, error: artErr } = await supabase
        .from('articles').select('*').eq('id', articleId).single();
      if (artErr) { setError(artErr.message); setLoading(false); return; }
      setArticle(cast(art));
      const { data: trans } = await supabase
        .from('article_translations').select('*')
        .eq('article_id', articleId).order('language');
      setTranslations(cast(trans) ?? []);
      setLoading(false);
    })();
  }, [articleId]);
  return { article, translations, loading, error };
}

// ── Save article ──────────────────────────────────────────────────
interface SaveArticleInput {
  id?: string; category: string; slug: string;
  cover_image_url?: string; video_url?: string;
  accent_block?: Record<string, unknown>;
  layout?: string;
  body_images?: { url: string; alt: string; format: string; type?: string }[];
  pull_quotes?: { text: string; side: string; language: string }[];
}

export async function saveArticle(input: SaveArticleInput): Promise<ArticleRow> {
  if (input.id) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.from('articles') as any).update({
      category: input.category, slug: input.slug,
      cover_image_url: input.cover_image_url ?? null, video_url: input.video_url ?? null,
      accent_block: input.accent_block ?? undefined,
      layout: input.layout ?? 'standard',
      body_images: input.body_images ?? undefined,
      pull_quotes: input.pull_quotes ?? undefined,
    }).eq('id', input.id).select().single();
    if (error) throw new Error(error.message);
    return cast(data);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('articles') as any).insert({
    category: input.category, slug: input.slug,
    cover_image_url: input.cover_image_url ?? null, video_url: input.video_url ?? null,
    author_id: (await supabase.auth.getUser()).data.user?.id, status: 'draft',
    accent_block: input.accent_block ?? { enabled: false, color: 'yellow', position: 'hero-bottom-right', variant: 'square' },
    layout: input.layout ?? 'standard',
    body_images: input.body_images ?? null,
    pull_quotes: input.pull_quotes ?? null,
  }).select().single();
  if (error) throw new Error(error.message);
  return cast(data);
}

// ── Delete article ────────────────────────────────────────────────
export async function deleteArticle(articleId: string): Promise<void> {
  // RLS: authors can only delete their own drafts;
  // editors/admins can delete any article.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from('articles') as any).delete().eq('id', articleId);
  if (error) throw new Error(error.message);
}

// ── Save translation ──────────────────────────────────────────────
interface SaveTranslationInput {
  article_id: string; id?: string; language: LanguageCode;
  headline: string; body: string; meta_description?: string;
}

export async function saveTranslation(input: SaveTranslationInput): Promise<TranslationRow> {
  if (input.id) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.from('article_translations') as any).update({
      headline: input.headline, body: input.body,
      meta_description: input.meta_description ?? null,
    }).eq('id', input.id).select().single();
    if (error) throw new Error(error.message);
    return cast(data);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('article_translations') as any).insert({
    article_id: input.article_id, language: input.language,
    headline: input.headline, body: input.body,
    meta_description: input.meta_description ?? null,
  }).select().single();
  if (error) throw new Error(error.message);
  return cast(data);
}
